import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, Autocomplete, Typography, Button, TextField, Tabs, Tab,
  DialogActions, CircularProgress, Box, Grid
} from '@mui/material';
import CloseIconButton from '../../../../reusable-components/basic-buttons/CloseIconButton';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import { TabPanel } from '../../../../reusable-components/tab-panel';
import { getOrderGuidesByFacilityAndCategory, saveOrderGuideVendorPreferences,  } from '../../../../redux/slices/orderGuides';
import { AwaitButton } from '../../../../reusable-components/await-button';
import { getOrderGuideVendorPreferencesData } from '../../../../api/order-guides';
import CustomDataGrid from '../../../../reusable-components/datagrid/CustomDataGrid';
import Iconify from '../../../../reusable-components/iconify/Iconify';
import { useDispatch } from '../../../../redux/store';

OrderGuideVendorPreferences.propTypes = {
  orderGuideData: PropTypes.object,
  orderGuideId: PropTypes.number,
  openVendorPreferences: PropTypes.bool,
  setOpenVendorPreferences: PropTypes.func,
};

export default function OrderGuideVendorPreferences({
  orderGuideData,
  orderGuideId,
  openVendorPreferences,
  setOpenVendorPreferences,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();

  const [vendorPreferences, setVendorPreferences] = useState(null);
  const [subcategories, setSubcategories] = useState(null);
  const [vendors, setVendors] = useState(null);
  const [selectedToDelete, setSelectedToDelete] = useState(null);
  const [tab, setTab] = useState(0);

  const cols = [
    {
      field: 'agoraSubcategory',
      headerName: 'Subcategory',
      minWidth: 80,
      flex: 1,
      type: 'customSelect',
    },
    {
      field: 'vendor',
      headerName: 'Vendor',
      minWidth: 80,
      flex: 1,
      type: 'customSelect',
    },
  ];

  useEffect(() => {
    if (!openVendorPreferences) return;
    setSelectedToDelete([]);
    if (orderGuideId === 0) setVendorPreferences(null);
    else if (orderGuideData) {
      setVendorPreferences(orderGuideData.preferences.map((preference, index) => ({ ...preference, id: index })));
      setSubcategories(orderGuideData.subcategories);
      setVendors(orderGuideData.vendors);
    } else {
      getOrderGuideVendorPreferencesData(orderGuideId)
        .then((res) => {
          setVendorPreferences(res.data.preferences.map((preference, index) => ({ ...preference, id: index })));
          setSubcategories(res.data.subcategories);
          setVendors(res.data.vendors);
        })
        .catch(() => enqueueSnackbar('Failed to load order guide vendor preferences', { variant: 'error' }));
    }
  }, [orderGuideData, orderGuideId, openVendorPreferences]);

  const defaultAutocompleteSettings = {
    size: 'small',
    fullWidth: true,
    isOptionEqualToValue: (option, value) => option?.value === value?.value,
    renderInput: (params) => <TextField {...params} label="Search or select" autoComplete="off"/>,
    sx: {
      '& legend': { display: 'none' },
      '& fieldset': { top: 0 },
      '& .MuiFormLabel-root ': { display: 'none' },
      '& span.MuiAutocomplete-tagSizeSmall ': { color: 'primary.main' },
    },
  };

  const defaultGirdSettings = {
    boxSX: { height: 300 },
    scrollbarHeight: 56,
    gridColumns: cols,
    isLoading: vendorPreferences === null,
    onRowSelectionModelChange: (newSelection) => {
      setSelectedToDelete(newSelection);
    },
    rowSelectionModel: selectedToDelete,
    checkboxSelection: true,
    displayCheckboxRight: true,
    CustomLeftToolbar: () => (
      <>
        <Box>{tab === 0 ? 'Preferred' : 'Restricted'} Vendor List</Box>
        {selectedToDelete?.length > 0 && (
          <Button
            size="small"
            color="error"
            variant="text"
            startIcon={<Iconify icon="ph:check-fill" />}
            onClick={() => {
              setVendorPreferences((preferences) => [
                ...preferences.filter((preference) => !selectedToDelete.includes(preference.id)),
              ]);
            }}
          >
            Delete selected items
          </Button>
        )}
      </>
    ),
  };

  const savePreferences = async () => {
    dispatch(
      saveOrderGuideVendorPreferences({
        id: orderGuideId,
        tempPreferences: vendorPreferences?.filter((preference) => !preference.isRestricted),
        tempRestricted: vendorPreferences?.filter((preference) => preference.isRestricted),
        closeAndReloadOrderGuide: () => {
          enqueueSnackbar('Vendor preferences saved', { variant: 'success' });
          if (orderGuideData) dispatch(getOrderGuidesByFacilityAndCategory(orderGuideId));
          setOpenVendorPreferences(false);
        },
      })
    );
  };
  
  const VendorSelects = ({isRestricted}) => (
    <Grid container mt={2} spacing={2}>
      {' '}
      {subcategories?.map((subcat) => (
        <Grid key={subcat.id} item xs={6}>
          <Box>{subcat.name}</Box>
          <Autocomplete
            {...defaultAutocompleteSettings}
            options={vendors ?? []}
            limitTags={isRestricted ? 3 : -1}
            multiple={isRestricted}
            value={
              isRestricted
                ? (vendorPreferences ?? [])
                    .filter(
                      (preference) =>
                        preference.isRestricted === isRestricted && preference.agoraSubcategoryId === subcat.id
                    )
                    .map((preference) => ({ value: preference.vendorId, label: preference.vendor }))
                : vendors?.find(
                    (vendor) =>
                      vendor.value ===
                      vendorPreferences?.find(
                        (pref) => pref.isRestricted === isRestricted && pref.agoraSubcategoryId === subcat.id
                      )?.vendorId
                  )
            }
            onChange={(e, newValue) => {
              setVendorPreferences((preferences) =>
                [
                  ...preferences.filter((preference) => preference.isRestricted !== isRestricted),
                  ...preferences.filter(
                    (preference) =>
                      preference.isRestricted === isRestricted && preference.agoraSubcategoryId !== subcat.id
                  ),
                  ...(newValue ? (isRestricted ? newValue : [newValue]) : []).map((value) => ({
                    agoraSubcategoryId: subcat.id,
                    agoraSubcategory: subcat.name,
                    vendor: value.label,
                    vendorId: value.value,
                    active: true,
                    isRestricted,
                  })),
                ].map((val, ind) => ({ ...val, id: ind }))
              );
            }}
          />
        </Grid>
      ))}
    </Grid>
  );

  const SELECT_ALL_OPTION = { value: 'ALL', label: 'Select All' };

  const AutofillSelect = ({isRestricted}) => {
    const optionsWithSelectAll = [SELECT_ALL_OPTION, ...(vendors ?? [])];

    const selectedVendors = (vendorPreferences ?? [])
      .filter((preference) => preference.isRestricted === isRestricted)
      .reduce((acc, preference) => {
        acc[preference.vendorId] = { value: preference.vendorId, label: preference.vendor };
        return acc;
      }, []);
    const selectedValues = Object.values(selectedVendors);

    const allSelected = vendors && selectedValues.length === vendors.length;
    const value = allSelected ? [SELECT_ALL_OPTION, ...selectedValues] : selectedValues;

    return (
      <>
        <Typography variant="subtitle1" sx={{ mt: 1, textAlign: 'center', fontSize: '16px' }}>
          {isRestricted ? 'Restricted Vendors' : 'Preferred Vendors'}
        </Typography>
        <Box>Autofill Vendor</Box>
        <Autocomplete
          {...defaultAutocompleteSettings}
          limitTags={10}
          options={optionsWithSelectAll}
          multiple
          value={value}
          renderInput={(params) => <TextField {...params} label="Autofill Vendor" autoComplete="off"/>}
          onChange={(e, newValue) => {
            const filtered = newValue.filter(
              (v, i, arr) => v.value !== 'ALL' && arr.findIndex((x) => x.value === v.value) === i
            );

            if (newValue.some((v) => v.value === 'ALL')) {
              setVendorPreferences((preferences) => {
                const vendorNameMap = vendors.reduce((acc, v) => {
                  acc[v.value] = v.label;
                  return acc;
                }, {});
                const newPrefs = vendors.flatMap((vendor) =>
                  subcategories.map((subcategory) => ({
                    agoraSubcategoryId: subcategory.id,
                    agoraSubcategory: subcategory.name,
                    vendor: vendor.label,
                    vendorId: vendor.value,
                    isRestricted,
                  }))
                );
                const filteredPrefs = preferences.filter((p) => p.isRestricted !== isRestricted);
                return [...filteredPrefs, ...newPrefs].map((preference, index) => ({ ...preference, id: index }));
              });
              return;
            }

            if (filtered.length === 0) {
              setVendorPreferences((preferences) =>
                preferences.filter((preference) => preference.isRestricted !== isRestricted)
              );
              return;
            }

            setVendorPreferences((preferences) => {
              const oldVendors = preferences
                .filter((preference) => preference.isRestricted === isRestricted)
                .map((val) => val.vendorId);
              const newValues = filtered.map((val) => val.value);
              const newVendors = newValues.filter((val) => !oldVendors.includes(val));
              const filteredPreferences = preferences.filter(
                (preference) => preference.isRestricted === isRestricted && newValues.includes(preference.vendorId)
              );
              if (newVendors.length === 0)
                return [
                  ...preferences.filter((preference) => preference.isRestricted !== isRestricted),
                  ...filteredPreferences,
                ];
              const vendorName = vendors.find((vendor) => vendor.value === newVendors[0])?.label;
              const oldSubcats = filteredPreferences
                .filter(
                  (preference) =>
                    preference.agoraSubcategoryId && (!isRestricted || preference.vendorId === newVendors[0])
                )
                .map((preference) => preference.agoraSubcategoryId);
              if (oldSubcats.length === subcategories.length)
                enqueueSnackbar('All possible categories already filled', { variant: 'error' });
              return [
                ...preferences.filter((preference) => preference.isRestricted !== isRestricted),
                ...filteredPreferences,
                ...subcategories
                  .filter((subcategory) => !oldSubcats.includes(subcategory.id))
                  .map((subcategory) => ({
                    agoraSubcategoryId: subcategory.id,
                    agoraSubcategory: subcategory.name,
                    vendor: vendorName,
                    vendorId: newVendors[0],
                    isRestricted,
                  })),
              ].map((preference, index) => ({ ...preference, id: index }));
            });
          }}
        />
      </>
    );
  };

  return (
    <Dialog open={openVendorPreferences} maxWidth="md" fullWidth>
      <DialogTitle>
        <Tabs value={tab} onChange={(event, newValue) => setTab(newValue)}>
          <Tab label="Preferred Vendor" />
          <Tab label="Restricted Vendors" />
        </Tabs>
        <CloseIconButton onClick={() => setOpenVendorPreferences(false)}/>
      </DialogTitle>
      {vendorPreferences ? (
        <DialogContent sx={{ width: '100%' }}>
          <TabPanel value={tab} index={0}>
            <AutofillSelect isRestricted={false}/>
            <VendorSelects isRestricted={false} />
            <Box sx={{ mt: 3 }}>
              <CustomDataGrid
                gridId="order-guide-vendor-preferences"
                data={vendorPreferences?.filter((preference) => !preference.isRestricted)}
                {...defaultGirdSettings}
              />
            </Box>
          </TabPanel>
          <TabPanel value={tab} index={1}>
            <AutofillSelect isRestricted />
            <VendorSelects isRestricted />
            <Box sx={{ mt: 3 }}>
              <CustomDataGrid
                gridId="order-guide-vendor-restricted"
                data={vendorPreferences?.filter((preference) => preference.isRestricted) ?? []}
                {...defaultGirdSettings}
              />
            </Box>
          </TabPanel>
        </DialogContent>
      ) : (
        <CircularProgress sx={{ position: 'absolute', left: '50%', top: '50%' }} />
      )}
      <DialogActions>
        <Button
          variant="outlined"
          color="error"
          size="medium"
          sx={{ width: 150, mr: 2 }}
          onClick={(e) => {
            e.stopPropagation();
            setOpenVendorPreferences(false);
          }}
        >
          Cancel
        </Button>
        <AwaitButton variant="contained" color="primary" size="medium" sx={{ width: 150 }} onClick={savePreferences}>
          Save
        </AwaitButton>
      </DialogActions>
    </Dialog>
  );
}
